import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'
import {
  trainerResourceFileSchema,
  trainerResourceSummarySchema,
} from './trainer-resource-read'

const optionalLegacyValue = z.unknown().optional()

/**
 * Transport contracts stay intentionally loose. Handlers keep the existing
 * request validation messages and status codes; these schemas only describe
 * the wire shape for clients and response parsing after the handler succeeds.
 */
export const trainerResourceWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.boolean(),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  providerHeadersReturned: z.boolean().optional(),
  objectKeyReturned: z.literal(false).optional(),
})

export const trainerResourceCreateRequestSchema = z.object({
  name: z.string(),
  desc: optionalLegacyValue,
  source_link: optionalLegacyValue,
  file_url: optionalLegacyValue,
  type: optionalLegacyValue,
  image_url: optionalLegacyValue,
  file_name: optionalLegacyValue,
  fileName: optionalLegacyValue,
  version: optionalLegacyValue,
  language: optionalLegacyValue,
  course_type: optionalLegacyValue,
  courseType: optionalLegacyValue,
  course_type_id: optionalLegacyValue,
  courseTypeId: optionalLegacyValue,
  category: optionalLegacyValue,
  available_for_ten: optionalLegacyValue,
  source_updated_date: optionalLegacyValue,
  sourceUpdatedDate: optionalLegacyValue,
  no_of_files: optionalLegacyValue,
  upload_id: optionalLegacyValue,
  upload: optionalLegacyValue,
}).passthrough()

export const trainerResourceRenameRequestSchema = z.object({
  name: z.string(),
}).passthrough()

export const trainerResourceArchiveRequestSchema = z.object({
  active: z.boolean(),
}).passthrough()

export const trainerResourceFileArchiveRequestSchema = trainerResourceArchiveRequestSchema

export const trainerResourceFileDownloadTrackRequestSchema = z.object({}).passthrough()

const trainerResourceIssuedUploadSchema = z.object({
  uploadId: z.string().min(1),
  presignedUrl: z.string().min(1),
  expiresAt: z.string().min(1),
  requiredHeaders: z.record(z.string()),
  providerHeadersReturned: z.boolean(),
  objectKeyReturned: z.literal(false),
}).passthrough()

export const trainerResourceCreateResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  resource: trainerResourceSummarySchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerResourceWriteSideEffectsSchema,
  upload: trainerResourceIssuedUploadSchema.optional(),
}).passthrough()

export const trainerResourceRenameResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  resource: trainerResourceSummarySchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerResourceWriteSideEffectsSchema,
}).passthrough()

export const trainerResourceArchiveResponseSchema = trainerResourceRenameResponseSchema

export const trainerResourceFileArchiveResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  resourceFile: trainerResourceFileSchema,
  idempotentReplay: z.boolean(),
  fileLifecycle: z.object({
    operation: z.enum(['archive', 'restore']),
    retentionMode: z.literal('db1_soft_archive'),
    providerDeleteCalled: z.literal(false),
    r2Touched: z.literal(false),
    destructiveCleanup: z.literal(false),
  }),
  sideEffects: trainerResourceWriteSideEffectsSchema,
}).passthrough()

export const trainerResourceDownloadSummarySchema = z.object({
  id: z.string().min(1),
  resourceFileId: z.string().nullable(),
  resourceFileName: z.string().nullable(),
  resourceFileUrl: z.string().nullable(),
  name: z.string().nullable(),
  viewerUserId: z.string().nullable(),
  viewerResolved: z.boolean(),
  idempotentReplay: z.boolean().optional(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerResourceFileDownloadTrackResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  download: trainerResourceDownloadSummarySchema,
  resourceFile: trainerResourceFileSchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerResourceWriteSideEffectsSchema,
}).passthrough()

export type TrainerResourceWriteSideEffects = z.infer<typeof trainerResourceWriteSideEffectsSchema>
export type TrainerResourceCreateRequest = z.infer<typeof trainerResourceCreateRequestSchema>
export type TrainerResourceRenameRequest = z.infer<typeof trainerResourceRenameRequestSchema>
export type TrainerResourceArchiveRequest = z.infer<typeof trainerResourceArchiveRequestSchema>
export type TrainerResourceFileArchiveRequest = z.infer<typeof trainerResourceFileArchiveRequestSchema>
export type TrainerResourceFileDownloadTrackRequest = z.infer<typeof trainerResourceFileDownloadTrackRequestSchema>
export type TrainerResourceCreateResponse = z.infer<typeof trainerResourceCreateResponseSchema>
export type TrainerResourceRenameResponse = z.infer<typeof trainerResourceRenameResponseSchema>
export type TrainerResourceArchiveResponse = z.infer<typeof trainerResourceArchiveResponseSchema>
export type TrainerResourceFileArchiveResponse = z.infer<typeof trainerResourceFileArchiveResponseSchema>
export type TrainerResourceFileDownloadTrackResponse = z.infer<typeof trainerResourceFileDownloadTrackResponseSchema>
